import { useState } from 'react'
import type { ReactNode } from 'react'
import {
  BarChart3,
  CalendarCheck,
  CalendarDays,
  CheckSquare,
  FileText,
  MapPin,
  PersonStanding,
  Shield,
  Sun,
  TrendingUp,
  Users,
} from 'lucide-react'
import type { CreateOrgType, SubscriptionTier } from '@/shared/domain'
import { cn } from '@/shared/ui/cn'
import { WingidIcon } from './wingid-icon'

export type OrgPlanPageProps = {
  orgType: CreateOrgType
  trialDays: number | null
  onContinue: (tier: SubscriptionTier) => void
  fullBleed?: boolean
  hideTrial?: boolean
}

type PlanTier = {
  monthlyPrice: number
  annualPrice: number
  saveBadge: string
}

const PLAN_TIERS: Record<CreateOrgType, PlanTier> = {
  club: { monthlyPrice: 125, annualPrice: 1200, saveBadge: 'Save 20%' },
  outfitter: { monthlyPrice: 179, annualPrice: 1750, saveBadge: 'Save 18%' },
  crew: { monthlyPrice: 49, annualPrice: 399, saveBadge: 'Save 32%' },
}

export type OrgPlanFeatureItem = {
  icon: ReactNode
  title: string
  description: string
}

const CLUB_FEATURES: OrgPlanFeatureItem[] = [
  {
    icon: <BarChart3 className="size-5" />,
    title: 'Harvest Tracking',
    description: 'Log birds by species, blind, hunter, and zone',
  },
  {
    icon: <CalendarDays className="size-5" />,
    title: 'Hunt & Event Calendar',
    description: 'Members book hunts, managers create events, and set up customer bookings',
  },
  {
    icon: <CheckSquare className="size-5" />,
    title: 'ToDo Lists',
    description: 'Track tasks, maintenance, and action items for your club',
  },
  {
    icon: <MapPin className="size-5" />,
    title: 'Property & Zone Management',
    description: 'Interactive map with parcels, zones, blinds, and scouting',
  },
  {
    icon: <Users className="size-5" />,
    title: 'Member Management',
    description: 'Roles, capacity limits, and activity by member',
  },
  {
    icon: <Sun className="size-5" />,
    title: 'Shooting Hours & Weather',
    description: 'Sunrise, sunset, and real-time conditions',
  },
  {
    icon: <FileText className="size-5" />,
    title: 'Waiver Storage',
    description: 'Store and manage signed waivers for members',
  },
]

const OUTFITTER_FEATURES: OrgPlanFeatureItem[] = [
  {
    icon: <CalendarCheck className="size-5" />,
    title: 'Bookings & Scheduling',
    description: 'Members book hunts, managers create events, and set up customer bookings',
  },
  {
    icon: <Users className="size-5" />,
    title: 'Customer Management',
    description: 'Manage customers, track bookings, and contact info',
  },
  {
    icon: <BarChart3 className="size-5" />,
    title: 'Harvest Tracking',
    description: 'Log birds by species, blind, hunter, and zone',
  },
  {
    icon: <MapPin className="size-5" />,
    title: 'Property & Zone Mapping',
    description: 'Interactive map with parcels, zones, blinds, and scouting',
  },
  {
    icon: <CheckSquare className="size-5" />,
    title: 'ToDo Lists',
    description: 'Track tasks, maintenance, and action items for your outfitter',
  },
  {
    icon: <PersonStanding className="size-5" />,
    title: 'Guide Assignments',
    description: 'Assign guides to trips and manage availability',
  },
  {
    icon: <Sun className="size-5" />,
    title: 'Shooting Hours & Weather',
    description: 'Sunrise, sunset, and real-time conditions',
  },
  {
    icon: <FileText className="size-5" />,
    title: 'Waiver Storage',
    description: 'Store and manage signed waivers for customers',
  },
]

const CREW_FEATURES: OrgPlanFeatureItem[] = [
  {
    icon: <BarChart3 className="size-5" />,
    title: 'Shared Harvest Log',
    description: 'Log birds by species, blind, hunter, and zone',
  },
  {
    icon: <CalendarDays className="size-5" />,
    title: 'Hunt & Event Calendar',
    description: 'Members book hunts, managers create events and coordinate season logistics',
  },
  {
    icon: <MapPin className="size-5" />,
    title: 'Simple Blind Reservation',
    description: 'Reserve blinds for your hunt day without complex draw mechanics',
  },
  {
    icon: <CheckSquare className="size-5" />,
    title: 'Shared To-Dos & Task Assignment',
    description: 'Track tasks, maintenance, and action items across your crew',
  },
  {
    icon: <Users className="size-5" />,
    title: 'Crew Members',
    description: 'Manage up to 15 seats with 6 included',
  },
  {
    icon: <MapPin className="size-5" />,
    title: 'Property & Zone Mapping',
    description: 'Interactive map with parcels, zones, blinds, and scouting — shared across the crew',
  },
  {
    icon: <Sun className="size-5" />,
    title: 'Shooting Hours & Weather',
    description: 'Sunrise, sunset, and real-time conditions',
  },
  {
    icon: <TrendingUp className="size-5" />,
    title: 'Wingman Group Insights',
    description: 'Squad-level harvest trends and activity insights',
  },
]

const COMING_SOON: OrgPlanFeatureItem[] = [
  {
    icon: <Shield className="size-5" />,
    title: 'Compliance',
    description: 'Harvest limit tracking and regulatory export reporting',
  },
  {
    icon: <TrendingUp className="size-5" />,
    title: 'Predictive Model',
    description: 'AI-powered harvest predictions and trend analysis',
  },
  {
    icon: <WingidIcon className="size-5" />,
    title: 'WingID',
    description: 'Snap a photo to ID any waterfowl species',
  },
]

const taglineByOrgType: Record<CreateOrgType, string> = {
  club: 'Your Club.\nYour Data.\nYour Season.',
  outfitter: 'Your Outfitter.\nYour Customers.\nYour Season.',
  crew: 'Your Crew.\nYour Hunt.\nYour Season.',
}

const heroImageByOrgType: Record<CreateOrgType, string> = {
  club: '/club_hero.jpg',
  outfitter: '/outfitter_hero.jpg',
  crew: '/crew_hero.jpg',
}

const featuresByOrgType: Record<CreateOrgType, OrgPlanFeatureItem[]> = {
  club: CLUB_FEATURES,
  outfitter: OUTFITTER_FEATURES,
  crew: CREW_FEATURES,
}

export function OrgPlanPage({
  orgType,
  trialDays,
  onContinue,
  fullBleed = false,
  hideTrial = false,
}: OrgPlanPageProps) {
  const [billingCycle, setBillingCycle] = useState<SubscriptionTier>('monthly')
  const tier = PLAN_TIERS[orgType]
  const heroImage = heroImageByOrgType[orgType]
  const tagline = taglineByOrgType[orgType]
  const features = featuresByOrgType[orgType]
  const trialLabel =
    orgType === 'outfitter'
      ? 'Free through September 1st, 2026'
      : trialDays
        ? `${trialDays}-day free trial`
        : 'Free trial'

  const isMonthly = billingCycle === 'monthly'
  const ctaPrice = isMonthly ? `$${tier.monthlyPrice}/mo` : `$${tier.annualPrice}/yr`

  return (
    <div
      className={cn('overflow-hidden bg-card', fullBleed ? '-mx-4 -mt-12' : 'rounded-xl border')}
      data-testid="feature-page"
    >
      <OrgPlanHero heroImage={heroImage} tagline={tagline} />

      <div className="flex flex-col gap-4 px-6 py-6">
        <div className="flex gap-1 rounded-xl bg-muted p-1">
          <button
            type="button"
            onClick={() => setBillingCycle('monthly')}
            className={`flex-1 rounded-lg px-3 py-2.5 text-center transition-colors ${
              isMonthly ? 'bg-primary text-primary-foreground' : 'text-foreground hover:bg-accent'
            }`}
          >
            <span className="block text-sm font-semibold">Monthly</span>
            <span
              className={`mt-0.5 block text-xs ${
                isMonthly ? 'text-primary-foreground/80' : 'text-muted-foreground'
              }`}
            >
              ${tier.monthlyPrice}/mo
            </span>
          </button>
          <button
            type="button"
            onClick={() => setBillingCycle('annual')}
            className={`flex-1 rounded-lg px-3 py-2.5 text-center transition-colors ${
              !isMonthly ? 'bg-primary text-primary-foreground' : 'text-foreground hover:bg-accent'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <span className="text-sm font-semibold">Annual</span>
              <span
                className={`rounded px-1.5 py-0.5 text-2xs font-bold ${
                  !isMonthly
                    ? 'bg-black/20 text-primary-foreground'
                    : 'bg-primary/20 text-primary'
                }`}
              >
                {tier.saveBadge}
              </span>
            </div>
            <span
              className={`mt-0.5 block text-xs ${
                !isMonthly ? 'text-primary-foreground/80' : 'text-muted-foreground'
              }`}
            >
              ${tier.annualPrice}/yr
            </span>
          </button>
        </div>

        <button
          type="button"
          onClick={() => onContinue(billingCycle)}
          data-testid="feature-page-continue"
          className="w-full rounded-lg bg-primary px-4 py-3.5 text-base font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
        >
          Continue — {ctaPrice}
        </button>

        <p className="text-center text-xs text-muted-foreground">Cancel anytime · Secure checkout</p>

        {hideTrial ? null : (
          <div className="text-center">
            <span className="inline-block rounded-md bg-primary/15 px-2.5 py-1 text-sm font-semibold text-primary">
              {trialLabel}
            </span>
          </div>
        )}

        <div className="mt-4">
          <p className="mb-4 text-xs font-bold uppercase tracking-[0.16em] text-primary">
            Included Now
          </p>
          <div className="flex flex-col gap-4">
            {features.map((feature) => (
              <OrgPlanFeatureRow key={feature.title} feature={feature} comingSoon={false} />
            ))}
          </div>
        </div>

        <div>
          <p className="mb-4 text-xs font-bold uppercase tracking-[0.16em] text-muted-foreground">
            Coming Soon
          </p>
          <div className="flex flex-col gap-4">
            {COMING_SOON.map((feature) => (
              <OrgPlanFeatureRow key={feature.title} feature={feature} comingSoon />
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export function OrgPlanHero({
  heroImage,
  tagline,
  insetTop = false,
}: {
  heroImage: string
  tagline: string
  insetTop?: boolean
}) {
  return (
    <div className="relative min-h-[200px] overflow-hidden">
      <img
        src={heroImage}
        alt=""
        aria-hidden
        className="absolute inset-0 size-full object-cover [filter:brightness(0.5)_saturate(0.8)]"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/70 to-card" />
      <div className={cn('relative flex flex-col gap-3 px-6 py-6', insetTop && 'pt-14')}>
        <span className="text-xs font-semibold uppercase tracking-[0.2em] text-white/70">
          BlindBook
        </span>
        <p className="whitespace-pre-line text-3xl font-extrabold leading-[1.15] text-white">
          {tagline}
        </p>
      </div>
    </div>
  )
}

export function OrgPlanFeatureRow({
  feature,
  comingSoon,
}: {
  feature: OrgPlanFeatureItem
  comingSoon: boolean
}) {
  return (
    <div className="flex gap-3">
      <div
        className={`flex size-10 shrink-0 items-center justify-center rounded-lg ${
          comingSoon ? 'bg-accent text-foreground' : 'bg-primary/15 text-primary'
        }`}
      >
        {feature.icon}
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-foreground">{feature.title}</span>
          {comingSoon ? (
            <span className="rounded bg-muted px-1.5 py-0.5 text-2xs font-bold uppercase tracking-[0.08em] text-muted-foreground">
              Coming Soon
            </span>
          ) : null}
        </div>
        <p className="mt-0.5 text-sm text-muted-foreground">{feature.description}</p>
      </div>
    </div>
  )
}
